import { faTrashCan } from '@fortawesome/free-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Image from 'next/image'
import Link from 'next/link'
import React, { useEffect, useRef, useState } from 'react'
import { useRemoveCartItem, useUpdateCartItem } from '@/hooks/business/cart'
import { debounce } from '~/helpers/debounce'
import { formatBRL } from '~/helpers/formatCurrency'
import { safeImageSrc } from '~/helpers/safeImageSrc'
import type { ICartItem } from '~/schemas/cart/cart'

interface IProductCartProps {
    data: ICartItem
    quantity: number
    /** Botões de quantidade e lixeira (o modal de produtos do checkout mostra o item sem eles). */
    displayControls?: boolean
}

/**
 * Item do carrinho (tela do carrinho e modal de produtos do checkout). A quantidade muda na tela na hora e vai para a
 * API meio segundo depois do último clique; se a API recusar (estoque), volta a quantidade do carrinho. Os avisos de
 * erro saem nos services.
 */
const ProductCart: React.FC<IProductCartProps> = ({ data, quantity, displayControls = true }) => {
    const [productQuantity, setQuantity] = useState(quantity)
    const { updateQuantity } = useUpdateCartItem()
    const { removeItem } = useRemoveCartItem()
    const debouncedChangeQuantityRef = useRef<(newQuantity: number) => void>(undefined)
    const productId = data.product.id

    useEffect(() => {
        debouncedChangeQuantityRef.current = debounce((newQuantity: number) => {
            updateQuantity(productId, newQuantity).catch(() => setQuantity(quantity))
        }, 500)
    }, [productId, quantity, updateQuantity])

    // A quantidade do carrinho mudou (resposta da API, outra aba): a do campo acompanha, ajustada na renderização.
    const [cartQuantity, setCartQuantity] = useState(quantity)
    if (cartQuantity !== quantity) {
        setCartQuantity(quantity)
        setQuantity(quantity)
    }

    const incrementProductQuantity = () => {
        const newQuantity = productQuantity + 1
        setQuantity(newQuantity)
        debouncedChangeQuantityRef.current?.(newQuantity)
    }

    const decrementProductQuantity = () => {
        if (productQuantity === 1) return
        const newQuantity = productQuantity - 1
        setQuantity(newQuantity)
        debouncedChangeQuantityRef.current?.(newQuantity)
    }

    const removeProduct = () => {
        removeItem(productId, productQuantity).catch(() => {})
    }

    return (
        <div className="flex flex-row pb-4 border-b border-submenu-gray" id={`product-${data.product.id}`}>
            {data.product.thumbnailUrl && (
                <Image
                    title={data.product.name}
                    src={safeImageSrc(data.product.thumbnailUrl)}
                    alt={`${data.product.name} - ${data.product.shortDescription}`}
                    className="w-32 mr-4 rounded-lg max-md:w-24 max-md:h-24"
                    width={1000}
                    height={1000}
                    quality={100}
                />
            )}
            <div className="flex flex-col flex-1 gap-0">
                <div className="flex justify-between gap-2">
                    {data.product.slug ? (
                        <Link
                            className="text-lg font-bold cursor-pointer text-blue-950 hover:underline"
                            href={`/produtos/${data.product.slug}`}
                        >
                            {data.product.name}
                        </Link>
                    ) : (
                        // Cópia antiga do produto no carrinho, sem slug: o item aparece sem link para a PDP.
                        <span className="text-lg font-bold text-blue-950">{data.product.name}</span>
                    )}
                    {displayControls && (
                        <button type="button" aria-label="Remover do carrinho" onClick={removeProduct}>
                            <FontAwesomeIcon
                                icon={faTrashCan}
                                color="rgb(23 37 84)"
                                className="p-1 text-xl cursor-pointer text-bold"
                            />
                        </button>
                    )}
                </div>
                <div className="flex justify-between pt-0 pb-2 text-gray-400 text-md max-md:text-xs">
                    <span className="font-normal">{data.product.shortDescription}</span>
                    <p className="">{formatBRL(data.product.discountedPrice)}</p>
                </div>

                {data.product.stockQuantity === 0 ? (
                    <p className="text-sm text-red">Infelizmente não possuímos este produto em estoque</p>
                ) : (
                    <>
                        <div className={`flex ${displayControls ? 'justify-between' : 'justify-end'} mt-2`}>
                            {displayControls && (
                                <div className="flex flex-row-reverse justify-between items-center bg-secondary/40 text-white text-center rounded-full px-2 w-[72px] h-7">
                                    <button
                                        data-action="increment"
                                        aria-label="Aumentar a quantidade"
                                        className={`w-6 h-20 bg-transparent rounded-b text-primary disabled:opacity-15 ${data.product.stockQuantity <= productQuantity ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                        type="button"
                                        onClick={incrementProductQuantity}
                                        disabled={data.product.stockQuantity <= productQuantity}
                                    >
                                        <Image src="/assets/icons/plus.svg" height={10} width={10} alt="plus" />
                                    </button>
                                    <input
                                        type="number"
                                        className="input-qtde h-[24px] bg-transparent text-white text-center w-full"
                                        step="1"
                                        max="2133"
                                        id="nuQty"
                                        name="Quantidade"
                                        min="1"
                                        value={productQuantity}
                                        readOnly
                                    />
                                    <button
                                        data-action="decrement"
                                        aria-label="Diminuir a quantidade"
                                        className="w-6 h-20 bg-transparent rounded-t outline-hidden cursor-pointer text-primary"
                                        onClick={decrementProductQuantity}
                                        type="button"
                                    >
                                        <Image src="/assets/icons/minus.svg" height={10} width={10} alt="minus" />
                                    </button>
                                </div>
                            )}

                            <p className="text-xl font-bold text-secondary">
                                {formatBRL(data.product.discountedPrice * quantity)}
                            </p>
                        </div>

                        {data.product.stockQuantity === productQuantity ? (
                            <p className="text-sm text-red">
                                Você alcançou a quantidade máxima em estoque para esse produto
                            </p>
                        ) : null}
                        {data.product.stockQuantity < productQuantity ? (
                            <p className="text-sm text-red">Não possuimos essa quantidade em estoque</p>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    )
}

export default ProductCart
